import React, { forwardRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Tooltip, type TooltipPlacement } from "@/components/Tooltip";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { PermissionEditorModal } from "@/components/PermissionEditorModal";

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    text?: string;
    variant?:
        | "basic"
        | "primary"
        | "primaryinverted"
        | "secondary"
        | "success"
        | "successinverted"
        | "danger"
        | "error"
        | "warning"
        | "warninginverted"
        | "outline"
        | "ghost"
        | "link"
        | "emerald"
        | "custom"
        | "black";
    size?: "xs" | "sm" | "md" | "lg" | "xl" | "none";
    icon?: React.ReactNode | string;
    startIcon?: React.ReactNode | string;
    endIcon?: React.ReactNode | string;
    loading?: boolean;
    isLoading?: boolean;
    loadingText?: string;
    fullWidth?: boolean;
    tooltip?: string;
    tooltipPlacement?: TooltipPlacement;
    rounded?: boolean;
    onlyicon?: boolean;
    contentClassName?: string;
    permissionKey?: string;
    iconStrokeWidth?: number;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            text,
            variant = "primary",
            size = "md",
            icon,
            startIcon,
            endIcon,
            iconStrokeWidth,
            loading = false,
            isLoading = false,
            loadingText,
            disabled = false,
            fullWidth = false,
            type = "button",
            onClick,
            onContextMenu,
            className = "",
            tooltip,
            tooltipPlacement = "top",
            rounded = false,
            onlyicon = false,
            contentClassName = "",
            permissionKey,
            ...props
        },
        ref
    ) => {
        const { permissionEditMode, setPermissionEditMode } = useAuth();
        const { showSuccess, showError } = useToast();
        const { t } = useTranslation("global");
        const [contextMenu, setContextMenu] = useState<{
            x: number;
            y: number;
        } | null>(null);
        const [permissionsOpen, setPermissionsOpen] = useState(false);
        const resolvedPermissionKey = permissionKey || "";
        const isBusy = loading || isLoading;
        const content = text || children;
        const isIconOnly = onlyicon || (!content && Boolean(icon || startIcon));
        const effectiveTooltip =
            tooltip ||
            (isIconOnly && props.title ? String(props.title) : undefined);

        const getIconSize = (): number => {
            switch (size) {
                case "xs":
                    return 14;
                case "sm":
                    return 16;
                case "lg":
                    return 20;
                case "xl":
                    return 24;
                case "none":
                    return 16;
                case "md":
                default:
                    return 18;
            }
        };

        const renderIconNode = (
            ic: React.ReactNode | string
        ): React.ReactNode => {
            if(!ic){
                return null;
            }
            if(typeof ic === "string"){
                if(onlyicon){
                    return (
                        <Icon
                            value={ic}
                            width="1em"
                            height="1em"
                            strokeWidth={iconStrokeWidth}
                        />
                    );
                }
                return (
                    <Icon
                        value={ic}
                        size={getIconSize()}
                        strokeWidth={iconStrokeWidth}
                    />
                );
            }
            if(React.isValidElement(ic)){
                const element = ic as React.ReactElement<{
                    width?: string | number;
                    height?: string | number;
                    size?: number;
                    strokeWidth?: number;
                }>;
                const elementProps = element.props;
                const extraProps: {
                    width?: string;
                    height?: string;
                    size?: number;
                    strokeWidth?: number;
                } = {};
                if(
                    iconStrokeWidth !== undefined &&
                    elementProps.strokeWidth === undefined
                ){
                    extraProps.strokeWidth = iconStrokeWidth;
                }
                if(onlyicon){
                    if(
                        !elementProps.width &&
                        !elementProps.height &&
                        !elementProps.size
                    ){
                        extraProps.width = "1em";
                        extraProps.height = "1em";
                    }
                    if(Object.keys(extraProps).length > 0){
                        return React.cloneElement(element, extraProps);
                    }
                    return ic;
                }
                if(!elementProps.size && size !== "none"){
                    extraProps.size = getIconSize();
                }
                if(Object.keys(extraProps).length > 0){
                    return React.cloneElement(element, extraProps);
                }
            }
            return ic;
        };

        const getOnlyIconVariantClasses = (): string => {
            switch (variant) {
                case "danger":
                case "error":
                    return "text-rose-500 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-200";
                case "warning":
                case "warninginverted":
                    return "text-amber-500 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-200";
                case "success":
                case "successinverted":
                case "emerald":
                    return "text-emerald-600 dark:text-emerald-400 hover:text-emerald-800 dark:hover:text-emerald-200";
                case "primary":
                case "primaryinverted":
                    return "text-primary hover:text-primary-dark";
                case "secondary":
                case "basic":
                case "ghost":
                case "link":
                case "outline":
                    return "text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-200";
                case "custom":
                    return "";
                case "black":
                    return "text-white";
                default:
                    return "text-primary hover:text-primary-dark";
            }
        };

        const getOnlyIconSizeClasses = (): string => {
            switch (size) {
                case "xs":
                    return "p-0 rounded-md text-[18px]";
                case "sm":
                    return "p-0 rounded-lg text-[20px]";
                case "lg":
                    return "p-0 rounded-xl text-[28px]";
                case "xl":
                    return "p-0 rounded-xl text-[32px]";
                case "none":
                    return "p-0";
                case "md":
                default:
                    return "p-0 rounded-lg text-[24px]";
            }
        };

        const baseClasses =
            size === "none" && variant === "custom" ? "" : "btn";

        const sizeClasses =
            size === "none"
                ? ""
                : {
                      xs: "btn-xs",
                      sm: "btn-sm",
                      md: "btn-md",
                      lg: "btn-lg",
                      xl: "btn-xl",
                  }[size] || "btn-md";

        const variantClasses =
            variant === "custom"
                ? ""
                : {
                      basic: "btn-secondary",
                      primary: "primary",
                      primaryinverted: "primaryinverted",
                      secondary: "secondary",
                      danger: "danger",
                      outline: "outline",
                      ghost: "ghost !border-transparent !shadow-none dark:text-zinc-300 dark:hover:bg-white/5",
                      link: "!border-transparent !shadow-none bg-transparent text-primary hover:underline p-0 h-auto active:scale-100",
                      emerald:
                          "bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs border border-transparent",
                      error: "danger",
                      warning: "warning",
                      warninginverted: "warninginverted",
                      success: "success",
                      successinverted: "successinverted",
                      black: "bg-black text-white border-transparent hover:bg-black/80",
                  }[variant] || "primary";

        const leadingIcon = icon || startIcon;

        const buttonProps = { ...props };
        if (effectiveTooltip) {
            delete buttonProps.title;
        }

        useEffect(() => {
            if (!contextMenu) {
                return undefined;
            }

            const closeMenu = (): void => setContextMenu(null);
            document.addEventListener("click", closeMenu);

            return () => document.removeEventListener("click", closeMenu);
        }, [contextMenu]);

        const handleContextMenu = (
            event: React.MouseEvent<HTMLButtonElement>
        ): void => {
            if (permissionEditMode) {
                event.preventDefault();
                event.stopPropagation();
                if (!permissionKey) {
                    showError(t("tools.permissions.missing_key"));
                    onContextMenu?.(event);
                    return;
                }
                setContextMenu({ x: event.clientX, y: event.clientY });
            }

            onContextMenu?.(event);
        };

        const classes = onlyicon
            ? `inline-flex items-center justify-center cursor-pointer select-none transition-colors duration-150 active:scale-90 disabled:opacity-40 disabled:cursor-not-allowed bg-transparent hover:bg-transparent border-transparent shrink-0 ${getOnlyIconSizeClasses()} ${getOnlyIconVariantClasses()} ${rounded ? "!rounded-full" : ""} ${className}`.trim()
            : `
            ${baseClasses} ${sizeClasses} ${variantClasses} ${fullWidth ? "w-full" : ""} ${className}
            ${rounded ? "!rounded-full !w-8 !h-8 !p-0 !min-w-0 !min-h-0 flex items-center justify-center" : ""}
        `.trim();

        const buttonElement = (
            <button
                ref={ref}
                type={type}
                disabled={disabled || isBusy}
                onClick={onClick}
                onContextMenu={handleContextMenu}
                className={classes}
                {...buttonProps}
                aria-label={
                    props["aria-label"] ||
                    (isIconOnly ? props.title || tooltip : undefined)
                }
            >
                {isBusy ? (
                    <>
                        <Icon
                            value="loader"
                            size={getIconSize()}
                            className="animate-spin shrink-0"
                        />
                        {!onlyicon && loadingText && (
                            <span className="leading-none">{loadingText}</span>
                        )}
                    </>
                ) : onlyicon ? (
                    <span className="shrink-0 inline-flex items-center justify-center">
                        {renderIconNode(leadingIcon || endIcon)}
                    </span>
                ) : (
                    <>
                        {leadingIcon && (
                            <span className="shrink-0 inline-flex items-center justify-center">
                                {renderIconNode(leadingIcon)}
                            </span>
                        )}
                        {content && (
                            <span
                                className={`inline-flex min-w-0 items-center justify-center gap-2 leading-none ${contentClassName}`}
                            >
                                {content}
                            </span>
                        )}
                        {endIcon && (
                            <span className="shrink-0 inline-flex items-center justify-center">
                                {renderIconNode(endIcon)}
                            </span>
                        )}
                    </>
                )}
            </button>
        );

        const debugMenu = contextMenu ? (
            <div
                className="fixed z-[9999] min-w-52 rounded-lg border border-gray-200 bg-white p-1.5 text-left shadow-xl dark:border-white/10 dark:bg-dark-card"
                style={{
                    left: Math.min(contextMenu.x, window.innerWidth - 240),
                    top: Math.min(contextMenu.y, window.innerHeight - 150),
                }}
                onClick={(event) => event.stopPropagation()}
            >
                <div className="border-b border-gray-100 px-3 py-2 text-xs text-gray-500 dark:border-white/10 dark:text-zinc-400">
                    {t("tools.contextual.button_label")}
                </div>
                <button
                    type="button"
                    className="mt-1 flex w-full items-center rounded-md px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 dark:text-zinc-200 dark:hover:bg-white/10"
                    onClick={() => {
                        setContextMenu(null);
                        setPermissionsOpen(true);
                    }}
                >
                    <div>{t("tools.permissions.edit_permissions")}</div>
                </button>
                <button
                    type="button"
                    className="flex w-full items-center rounded-md px-3 py-2 text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-500/10"
                    onClick={() => {
                        setPermissionEditMode(false);
                        setContextMenu(null);
                        showSuccess(t("tools.permissions.disabled_message"));
                    }}
                >
                    <div>{t("tools.permissions.exit_mode")}</div>
                </button>
            </div>
        ) : null;

        if (effectiveTooltip) {
            return (
                <>
                    <Tooltip title={effectiveTooltip} placement={tooltipPlacement}>
                        {disabled ? (
                            <span className="inline-block">{buttonElement}</span>
                        ) : (
                            buttonElement
                        )}
                    </Tooltip>
                    {debugMenu}
                    <PermissionEditorModal
                        isOpen={permissionsOpen}
                        itemKey={resolvedPermissionKey}
                        onClose={() => setPermissionsOpen(false)}
                    />
                </>
            );
        }

        return (
            <>
                {buttonElement}
                {debugMenu}
                <PermissionEditorModal
                    isOpen={permissionsOpen}
                    itemKey={resolvedPermissionKey}
                    onClose={() => setPermissionsOpen(false)}
                />
            </>
        );
    }
);

Button.displayName = "Button";
export default Button;
